import React from 'react';
import type { DiscoveryFilters } from '../../types/discoveryBadges';
import VehicleFieldCombobox from '../VehicleFieldCombobox';
import type { VehicleSuggestField } from '../VehicleFieldCombobox';
import { FilterSection } from '../ui/DirectoryBrowseLayout';

export const BUILD_PURPOSES: [string, string][] = [
  ['street', 'Street'],
  ['daily', 'Daily'],
  ['track', 'Track'],
  ['time_attack', 'Time attack'],
  ['drag', 'Drag'],
  ['drift', 'Drift'],
  ['rally', 'Rally'],
  ['autocross', 'Autocross'],
  ['show', 'Show'],
  ['stance', 'Stance'],
  ['off_road', 'Off-road'],
  ['overland', 'Overland'],
  ['restoration', 'Restoration'],
  ['oem_plus', 'OEM+'],
  ['touring', 'Touring'],
  ['competition', 'Competition'],
  ['mixed_use', 'Mixed use'],
];

const EXTERIOR_TYPES: [string, string][] = [
  ['paint', 'Paint'],
  ['wrap', 'Wrap'],
  ['ppf', 'PPF'],
];

const DRIVETRAINS = ['FWD', 'RWD', 'AWD', '4WD'] as const;

function toggleCsvValue(current: string | undefined, value: string): string | undefined {
  const parts = (current || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const exists = parts.some((p) => p.toLowerCase() === value.toLowerCase());
  const next = exists
    ? parts.filter((p) => p.toLowerCase() !== value.toLowerCase())
    : [...parts, value];
  return next.length ? next.join(',') : undefined;
}

function csvHas(current: string | undefined, value: string): boolean {
  return (current || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .includes(value.toLowerCase());
}

interface DiscoverFiltersPanelProps {
  draft: DiscoveryFilters;
  onChange: (next: DiscoveryFilters) => void;
}

/**
 * Expanded Discover filter form — vehicle attributes, build signals, and community facets.
 * Multi-select fields encode as comma-separated values in the URL/API.
 */
export const DiscoverFiltersPanel: React.FC<DiscoverFiltersPanelProps> = ({ draft, onChange }) => {
  const set = <K extends keyof DiscoveryFilters>(key: K, value: DiscoveryFilters[K]) => {
    onChange({ ...draft, [key]: value });
  };

  return (
    <div className="space-y-4">
      <FilterSection id="vehicle" label="Vehicle">
        {(
          [
            ['make', 'Make', 'Comma-separate for multiple'],
            ['model', 'Model', 'Comma-separate for multiple'],
            ['trim', 'Trim', ''],
            ['color', 'Color', ''],
          ] as const
        ).map(([key, label, hint]) => (
          <label key={key} className="block text-sm text-gray-300">
            {label}
            <input
              className="mt-1 w-full input min-h-[40px]"
              value={(draft[key] as string) || ''}
              onChange={(e) => set(key, (e.target.value || undefined) as DiscoveryFilters[typeof key])}
              placeholder={hint || undefined}
              autoComplete="off"
            />
          </label>
        ))}

        {(
          [
            ['chassis', 'Chassis'],
            ['engine', 'Engine'],
            ['transmission', 'Transmission'],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="block text-sm text-gray-300">
            {label}
            <VehicleFieldCombobox
              field={key as VehicleSuggestField}
              id={`discover-filter-${key}`}
              value={(draft[key] as string) || ''}
              onChange={(v) => set(key, (v.trim() || undefined) as DiscoveryFilters[typeof key])}
              allowCreate={false}
              placeholder={`Existing ${label.toLowerCase()} values…`}
              className="mt-1 input min-h-[40px]"
            />
          </label>
        ))}

        <fieldset>
          <legend className="text-sm text-gray-300 mb-2">Drivetrain</legend>
          <div className="flex flex-wrap gap-2">
            {DRIVETRAINS.map((d) => (
              <button
                key={d}
                type="button"
                className={csvHas(draft.drivetrain, d) ? 'filter-chip-active' : 'filter-chip'}
                onClick={() => set('drivetrain', toggleCsvValue(draft.drivetrain, d))}
                aria-pressed={csvHas(draft.drivetrain, d)}
              >
                {d}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-sm text-gray-300">
            Year min
            <input
              inputMode="numeric"
              className="mt-1 w-full input min-h-[40px]"
              value={draft.year_min ?? ''}
              onChange={(e) => set('year_min', e.target.value || undefined)}
            />
          </label>
          <label className="text-sm text-gray-300">
            Year max
            <input
              inputMode="numeric"
              className="mt-1 w-full input min-h-[40px]"
              value={draft.year_max ?? ''}
              onChange={(e) => set('year_max', e.target.value || undefined)}
            />
          </label>
        </div>
      </FilterSection>

      <FilterSection id="exterior" label="Exterior" defaultOpen={false}>
        <fieldset>
          <legend className="text-sm text-gray-300 mb-2">Finish type</legend>
          <div className="flex flex-wrap gap-2">
            {EXTERIOR_TYPES.map(([val, label]) => (
              <button
                key={val}
                type="button"
                className={draft.exterior_finish_type === val ? 'filter-chip-active' : 'filter-chip'}
                onClick={() =>
                  set('exterior_finish_type', draft.exterior_finish_type === val ? undefined : val)
                }
                aria-pressed={draft.exterior_finish_type === val}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="block text-sm text-gray-300">
          Finish color
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.exterior_finish_color || ''}
            onChange={(e) => set('exterior_finish_color', e.target.value || undefined)}
          />
        </label>
        <label className="block text-sm text-gray-300">
          Finish brand
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.exterior_finish_brand || ''}
            onChange={(e) => set('exterior_finish_brand', e.target.value || undefined)}
          />
        </label>
      </FilterSection>

      <FilterSection id="build" label="Build">
        <fieldset>
          <legend className="text-sm text-gray-300 mb-2">Status</legend>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['planned', 'Planned'],
                ['in_progress', 'In progress'],
                ['complete', 'Complete'],
              ] as const
            ).map(([val, label]) => (
              <button
                key={val}
                type="button"
                className={csvHas(draft.build_status, val) ? 'filter-chip-active' : 'filter-chip'}
                onClick={() => set('build_status', toggleCsvValue(draft.build_status, val))}
                aria-pressed={csvHas(draft.build_status, val)}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="text-sm text-gray-300 mb-2">Purpose</legend>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
            {BUILD_PURPOSES.map(([val, label]) => (
              <button
                key={val}
                type="button"
                className={csvHas(draft.build_purpose, val) ? 'filter-chip-active' : 'filter-chip'}
                onClick={() => set('build_purpose', toggleCsvValue(draft.build_purpose, val))}
                aria-pressed={csvHas(draft.build_purpose, val)}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="block text-sm text-gray-300">
          Min documentation
          <select
            className="mt-1 w-full select-surface min-h-[40px] text-gray-200"
            value={String(draft.doc_score_min ?? '')}
            onChange={(e) => set('doc_score_min', e.target.value || undefined)}
          >
            <option value="">Any</option>
            <option value="50">50+</option>
            <option value="75">75+</option>
            <option value="90">90+</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-sm text-gray-300">
            Power min
            <input
              inputMode="numeric"
              className="mt-1 w-full input min-h-[40px]"
              value={draft.power_min ?? ''}
              onChange={(e) => set('power_min', e.target.value || undefined)}
              placeholder="whp"
            />
          </label>
          <label className="text-sm text-gray-300">
            Power max
            <input
              inputMode="numeric"
              className="mt-1 w-full input min-h-[40px]"
              value={draft.power_max ?? ''}
              onChange={(e) => set('power_max', e.target.value || undefined)}
              placeholder="whp"
            />
          </label>
        </div>
        <label className="block text-sm text-gray-300">
          Min power / weight
          <input
            inputMode="numeric"
            className="mt-1 w-full input min-h-[40px]"
            value={draft.ptw_min ?? ''}
            onChange={(e) => set('ptw_min', e.target.value || undefined)}
            placeholder="hp/ton"
          />
        </label>
      </FilterSection>

      <FilterSection id="mods" label="Mods & parts" defaultOpen={false}>
        <label className="block text-sm text-gray-300">
          Mod brand
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.mod_brand || ''}
            onChange={(e) => set('mod_brand', e.target.value || undefined)}
          />
        </label>
        <label className="block text-sm text-gray-300">
          Mod category
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.mod_category || ''}
            onChange={(e) => set('mod_category', e.target.value || undefined)}
          />
        </label>
      </FilterSection>

      <FilterSection id="community" label="Community" defaultOpen={false}>
        <label className="block text-sm text-gray-300">
          Event slug
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.event_slug || ''}
            onChange={(e) => set('event_slug', e.target.value || undefined)}
          />
        </label>
        <label className="block text-sm text-gray-300">
          Award title
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.award_title || ''}
            onChange={(e) => set('award_title', e.target.value || undefined)}
          />
        </label>
        <label className="block text-sm text-gray-300">
          Club slug
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.club_slug || ''}
            onChange={(e) => set('club_slug', e.target.value || undefined)}
          />
        </label>
        <label className="block text-sm text-gray-300">
          Badge category
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.badge_category || ''}
            onChange={(e) => set('badge_category', e.target.value || undefined)}
          />
        </label>
        <label className="block text-sm text-gray-300">
          Achievement
          <input
            className="mt-1 w-full input min-h-[40px]"
            value={draft.achievement || ''}
            onChange={(e) => set('achievement', e.target.value || undefined)}
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm text-gray-300">
            Spec key
            <input
              className="mt-1 w-full input min-h-[40px]"
              value={draft.spec_key || ''}
              onChange={(e) => set('spec_key', e.target.value || undefined)}
            />
          </label>
          <label className="block text-sm text-gray-300">
            Spec value
            <input
              className="mt-1 w-full input min-h-[40px]"
              value={draft.spec_value || ''}
              onChange={(e) => set('spec_value', e.target.value || undefined)}
            />
          </label>
        </div>
      </FilterSection>

      <FilterSection id="signals" label="Signals">
        {(
          [
            ['has_images', 'Has photos'],
            ['has_modifications', 'Has modifications'],
            ['has_awards', 'Has awards'],
            ['verified_club', 'Verified club'],
            ['certified', 'Certified builds'],
            ['has_verified_dyno', 'Verified dyno'],
            ['has_performance_record', 'Performance records'],
            ['has_achievement', 'Has achievements'],
            ['has_shop_contributor', 'Shop built'],
            ['has_albums', 'Has albums'],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm text-gray-300 min-h-[40px] cursor-pointer">
            <input
              type="checkbox"
              checked={draft[key] === true || draft[key] === 'true'}
              onChange={(e) => set(key, e.target.checked ? 'true' : undefined)}
              className="checkbox-control"
            />
            {label}
          </label>
        ))}
      </FilterSection>
    </div>
  );
};

export default DiscoverFiltersPanel;
